"use client";

import { useState } from "react";
import Link from "next/link";
import Icon from "@/components/ui/AppIcon";
import AppImage from "@/components/ui/AppImage";

interface RegisterFormData {
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
}

export default function RegisterPage() {
  const [formData, setFormData] = useState<RegisterFormData>({
    fullName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    agreeToTerms: false,
  });

  const [errors, setErrors] = useState<Partial<RegisterFormData>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (field: keyof RegisterFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<RegisterFormData> = {};

    if (!formData.fullName) {
      newErrors.fullName = "Fullt navn er påkrevd";
    } else if (formData.fullName.length < 2) {
      newErrors.fullName = "Navnet må være minst 2 tegn";
    }

    if (!formData.email) {
      newErrors.email = "E-postadresse er påkrevd";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Ugyldig e-postformat";
    }

    if (!formData.phone) {
      newErrors.phone = "Telefonnummer er påkrevd";
    } else if (!/^\d{8}$/.test(formData.phone.replace(/\D/g, ""))) {
      newErrors.phone = "Telefonnummer må være 8 siffer";
    }

    if (!formData.password) {
      newErrors.password = "Passord er påkrevd";
    } else if (formData.password.length < 8) {
      newErrors.password = "Passordet må være minst 8 tegn";
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = "Passordet må inneholde store og små bokstaver og tall";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Bekreft passord er påkrevd";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passordene samsvarer ikke";
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = "Du må godta vilkårene" as any;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Mock registration - in real implementation, this would call registration API
      console.log("Registration attempt:", formData);
      alert("Registrering vellykket! (Mock-implementering)");
    }
  };

  const getPasswordStrength = () => {
    const password = formData.password;
    if (!password) return { strength: 0, label: "" };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z\d]/.test(password)) strength++;

    const labels = ["Svakt", "Middels", "Sterkt"];
    const colors = ["bg-error", "bg-warning", "bg-success"];
    
    return {
      strength: Math.min(strength, 3),
      label: labels[Math.min(Math.floor(strength / 2), 2)],
      color: colors[Math.min(Math.floor(strength / 2), 2)],
    };
  };

  const passwordStrength = getPasswordStrength();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-2xl">
        {/* Logo and Header */}
        <div className="text-center mb-8 animate-fade-in">
          <Link href="/homepage" className="inline-flex items-center gap-3 group mb-6">
            <AppImage
              src="/assets/images/FullLogo_Transparent-1770223924492.png"
              alt="Oslo Bil Auto AS Logo"
              width={200}
              height={50}
              className="h-12 w-auto object-contain"
            />
          </Link>
          <h1 className="text-3xl font-bold text-foreground mb-2">Opprett konto</h1>
          <p className="text-muted-foreground">Registrer deg for å komme i gang med kjøretøysporing</p>
        </div>

        {/* Registration Form */}
        <div className="glass border border-border rounded-2xl p-8 shadow-soft animate-scale-in">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Fullt navn *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => handleChange("fullName", e.target.value)}
                  placeholder="Ola Nordmann"
                  className={`w-full px-4 py-3 pl-11 bg-background border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring ${
                    errors.fullName ? "border-error" : "border-border"
                  }`}
                />
                <Icon
                  name="UserIcon"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  size={20}
                />
              </div>
              {errors.fullName && (
                <p className="mt-1 text-sm text-error flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {errors.fullName}
                </p>
              )}
            </div>

            {/* Email and Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  E-postadresse *
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="din@epost.no"
                    className={`w-full px-4 py-3 pl-11 bg-background border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring ${
                      errors.email ? "border-error" : "border-border"
                    }`}
                  />
                  <Icon
                    name="EnvelopeIcon"
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    size={20}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-error flex items-center gap-1">
                    <Icon name="ExclamationCircleIcon" size={16} />
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Telefonnummer *
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="12345678"
                    className={`w-full px-4 py-3 pl-11 bg-background border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring ${
                      errors.phone ? "border-error" : "border-border"
                    }`}
                  />
                  <Icon
                    name="PhoneIcon"
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    size={20}
                  />
                </div>
                {errors.phone && (
                  <p className="mt-1 text-sm text-error flex items-center gap-1">
                    <Icon name="ExclamationCircleIcon" size={16} />
                    {errors.phone}
                  </p>
                )}
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Passord *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  placeholder="Minst 8 tegn"
                  className={`w-full px-4 py-3 pl-11 pr-11 bg-background border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring ${
                    errors.password ? "border-error" : "border-border"
                  }`}
                />
                <Icon
                  name="LockClosedIcon"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  size={20}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name={showPassword ? "EyeSlashIcon" : "EyeIcon"} size={20} />
                </button>
              </div>
              {formData.password && (
                <div className="mt-2">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                        style={{ width: `${(passwordStrength.strength / 3) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-muted-foreground">
                      {passwordStrength.label}
                    </span>
                  </div>
                </div>
              )}
              {errors.password && (
                <p className="mt-1 text-sm text-error flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {errors.password}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Bekreft passord *
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange("confirmPassword", e.target.value)}
                  placeholder="Skriv inn passordet på nytt"
                  className={`w-full px-4 py-3 pl-11 pr-11 bg-background border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring ${
                    errors.confirmPassword ? "border-error" : "border-border"
                  }`}
                />
                <Icon
                  name="LockClosedIcon"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  size={20}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name={showConfirmPassword ? "EyeSlashIcon" : "EyeIcon"} size={20} />
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-error flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            {/* Terms Agreement */}
            <div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.agreeToTerms}
                  onChange={(e) => handleChange("agreeToTerms", e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-ring"
                />
                <span className="text-sm text-muted-foreground">
                  Jeg godtar{" "}
                  <Link href="#" className="text-primary hover:text-primary/80 font-medium">
                    vilkårene for bruk
                  </Link>{" "}
                  og{" "}
                  <Link href="#" className="text-primary hover:text-primary/80 font-medium">
                    personvernreglene
                  </Link>
                </span>
              </label>
              {errors.agreeToTerms && (
                <p className="mt-1 text-sm text-error flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={16} />
                  {errors.agreeToTerms}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all shadow-lg hover:shadow-glow flex items-center justify-center gap-2"
            >
              Opprett konto
              <Icon name="ArrowRightIcon" size={20} />
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-card text-muted-foreground">eller</span>
            </div>
          </div>

          {/* Login Link */}
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Har du allerede en konto?{" "}
              <Link
                href="/login"
                className="font-semibold text-primary hover:text-primary/80 transition-colors"
              >
                Logg inn her
              </Link>
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <Link
            href="/homepage"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
          >
            <Icon name="ArrowLeftIcon" size={16} />
            Tilbake til hjemmesiden
          </Link>
        </div>
      </div>
    </div>
  );
}